import React, { useState, Dispatch, SetStateAction } from 'react';

import { ScrollView, TouchableOpacity } from 'react-native';

import { isScrollCloseToBottom } from '../../../utils';
import { SearchEmployerCandidatesQueryResult } from '../../../graphql/generated';
import { LogoLoader } from '../../../features/Loading/LogoLoader';

import {
  FOHCircleLoader,
  FOHColors,
  FOHLabel,
  FOHSmallDetailLabel,
  FOHSpace,
  FOHSquareTag,
  FOHView
} from '../../ingredients';
import {
  FOH_INPUT_HEIGHT,
  OPTION_LIST_PADDING,
  OPTION_MARGIN_BOT,
  OPTION_MIN_HEIGHT
} from '../../constants';
import { useClickOutside } from '../../spices';

import { FOHSearchBar } from '../search/FOHSearchBar';

import { OptionsContainer } from './FOHSearchableSelectDropdown';

interface CandidateSearchResult {
  node: {
    employerCandidateId: string;
    userId: string;
    firstName: string;
    lastName: string;
    email: string;
    positionId: string;
    positionName: string;
    locationId: string;
    locationName: string;
    stageId: string;
    isSelectable: boolean;
    isArchived: boolean;
    __typename: string;
  };
}

interface FOHCandidateSearchDropdownProps {
  isResultsVisible: boolean;
  setIsResultsVisible: Dispatch<SetStateAction<boolean>>;
  searchValue: string;
  setSearchValue: Dispatch<SetStateAction<string>>;
  searchPlaceholder: string;
  candidateSearchResults: CandidateSearchResult[];
  fetchMoreSearchResults: () => Promise<SearchEmployerCandidatesQueryResult>;
  onPressSearchedCandididate: (candidate: any) => void;
}

export const FOHCandidateSearchDropdown = (
  props: FOHCandidateSearchDropdownProps
) => {
  const {
    isResultsVisible,
    setIsResultsVisible,
    searchValue,
    setSearchValue,
    searchPlaceholder,
    candidateSearchResults,
    fetchMoreSearchResults,
    onPressSearchedCandididate
  } = props;

  const [loadingMoreResults, setLoadingMoreResults] = useState<boolean>(false);

  const [ref] = useClickOutside({
    close: () => {
      if (isResultsVisible) {
        setSearchValue('');
        setIsResultsVisible(false);
      }
    }
  });

  return (
    <FOHView ref={ref} style={{ width: '40%', alignItems: 'flex-end' }}>
      <FOHSearchBar
        searchPlaceholder={searchPlaceholder}
        onChangeSearch={setSearchValue}
        searchValue={searchValue}
        clearSearch={() => setSearchValue('')}
        style={{
          maxWidth: 500
        }}
        testID="global-candidate-searchbar"
      />
      {isResultsVisible && (
        <OptionsContainer
          shadow={true}
          style={{
            top: FOH_INPUT_HEIGHT,
            maxWidth: 500
          }}
        >
          {candidateSearchResults.length > 0 ? (
            <ScrollView
              style={{
                maxHeight:
                  OPTION_LIST_PADDING * 2 +
                  OPTION_MIN_HEIGHT * 3 +
                  OPTION_MARGIN_BOT * 3,
                paddingHorizontal: 12,
                paddingTop: OPTION_LIST_PADDING - 8,
                paddingBottom: 12
              }}
              scrollEventThrottle={16}
              onScroll={async ({ nativeEvent }) => {
                if (isScrollCloseToBottom(nativeEvent)) {
                  setLoadingMoreResults(true);
                  await fetchMoreSearchResults();
                  setLoadingMoreResults(false);
                }
              }}
            >
              {candidateSearchResults.map(result => {
                const candidate = result.node;
                const disabled = !candidate.isSelectable;

                return (
                  <TouchableOpacity
                    key={candidate.employerCandidateId}
                    disabled={disabled}
                    onPress={() => onPressSearchedCandididate(candidate)}
                    testID={`global-search-option-${candidate.firstName}`}
                  >
                    <FOHView
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        padding: 12,
                        backgroundColor: candidate.isArchived
                          ? FOHColors.GRAYSCALE_100
                          : FOHColors.WHITE,
                        borderBottomWidth: candidate.isArchived ? 2 : 0,
                        borderBottomColor: FOHColors.WHITE
                      }}
                    >
                      <FOHView style={{ width: '80%' }}>
                        <FOHLabel
                          style={{
                            color: disabled
                              ? FOHColors.GRAYSCALE_500
                              : FOHColors.GRAYSCALE_900
                          }}
                        >{`${candidate.firstName} ${candidate.lastName} - ${candidate.email}`}</FOHLabel>
                        <FOHSmallDetailLabel
                          style={{
                            color: disabled
                              ? FOHColors.GRAYSCALE_500
                              : FOHColors.GRAYSCALE_900
                          }}
                        >
                          {`Current Match: ${candidate.positionName} - ${candidate.locationName}`}
                        </FOHSmallDetailLabel>
                      </FOHView>
                      {candidate.isArchived && (
                        <FOHSquareTag
                          style={{
                            width: '15%',
                            backgroundColor: FOHColors.GRAYSCALE_500,
                            color: FOHColors.WHITE
                          }}
                          label="Archived"
                        />
                      )}
                    </FOHView>
                  </TouchableOpacity>
                );
              })}
              {loadingMoreResults && (
                <FOHView
                  style={{
                    alignItems: 'center',
                    width: '100%',
                    paddingTop: 8
                  }}
                >
                  <LogoLoader />
                  <FOHSpace />
                </FOHView>
              )}
            </ScrollView>
          ) : (
            <FOHView style={{ marginVertical: 10 }}>
              <FOHCircleLoader />
            </FOHView>
          )}
        </OptionsContainer>
      )}
    </FOHView>
  );
};
